import { takeUntil, filter } from 'rxjs/operators';
import type { History } from '@o2d/history';

import type { WhiteboardBus } from '../../core/bus/whiteboard-bus.js';
import type { UiAction } from '../../core/bus/ui-action.events.js';
import { objectId } from '../../core/bus/branded-types.js';
import type { BoardState } from '../../core/state/board-state.js';
import { AddObjectCommand } from '../../core/command/add-object.command.js';
import { createStickyDefaults, type CreateStickyDefaultsOptions } from './sticky-defaults.factory.js';

/**
 * Subscribes to `place/sticky` UiActions and materialises a new sticky:
 * builds the StickyNote via `createStickyDefaults`, pushes an
 * `AddObjectCommand` onto history, then re-emits `selection/set` and
 * `edit/enter-sticky` for the new id (TR6).
 *
 * Lifecycle: subscription is bound to `bus.destroy$` via `takeUntil`. The
 * controller is NOT a Lit ReactiveController — its lifetime equals the bus.
 *
 * FC4: never publishes onto `selection$`/`tool$`/`viewport$` directly; downstream
 * adapters translate `selection/set` into a `selection$` write.
 */
export class StickyPlacementController {
    readonly #bus: WhiteboardBus;
    readonly #state: BoardState;
    readonly #history: History<BoardState>;

    constructor(bus: WhiteboardBus, state: BoardState, history: History<BoardState>) {
        this.#bus = bus;
        this.#state = state;
        this.#history = history;

        this.#bus.uiActions$
            .pipe(
                filter(
                    (a): a is Extract<UiAction, { type: 'place/sticky' }> =>
                        a.type === 'place/sticky',
                ),
                takeUntil(this.#bus.destroy$),
            )
            .subscribe({
                next: (action) => this.#handlePlace(action),
                error: () => {
                    if (import.meta.env?.DEV) {
                        console.warn('[StickyPlacementController] bus error swallowed');
                    }
                },
            });
    }

    #handlePlace(action: Extract<UiAction, { type: 'place/sticky' }>): void {
        const scale = this.#bus.viewport$.value.scale;
        // Stefan, 2026-09-03 — "choose properties before drawing", see the
        // identical comment in rectangle-placement.controller.ts.
        const staged = this.#bus.stagedToolProperties$.value.sticky;
        const overrides: CreateStickyDefaultsOptions | undefined = staged && {
            color: staged['color'] as string | undefined,
        };
        const sticky = createStickyDefaults(
            scale,
            { x: action.worldX, y: action.worldY },
            action.size ?? null,
            overrides,
        );

        const cmd = new AddObjectCommand(sticky);
        this.#history.push(cmd, this.#state);

        const newId = objectId(sticky.id);
        this.#bus.uiActions$.next({ type: 'selection/set', ids: [newId] });
        this.#bus.uiActions$.next({ type: 'edit/enter-sticky', id: newId });
    }
}
